import { useEffect, useState } from "react"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { Capacitor } from "@capacitor/core"
import { toast } from "sonner"
import { ChevronRight, Lock, LockOpen } from "@/components/icons"
import { TopBarBackButton } from "@/components/topbar/TopBarBackButton"
import { AppLayout } from "@/components/AppLayout"
import { Card } from "@/components/ui/card"
import { MenuItem } from "@/components/ui/menu-item"
import { PasscodeEntry } from "@/components/passcode/PasscodeEntry"
import { removePasscode, setPasscode, usePasscodeEnabled, verifyPasscode } from "@/lib/passcode"

export const Route = createFileRoute("/_auth/profile/settings/passcode")({
  beforeLoad: () => {
    // Passcode is a native-app lock; the settings row is hidden on web.
    if (!Capacitor.isNativePlatform()) {
      throw redirect({ to: "/profile/settings" })
    }
  },
  component: RouteComponent,
})

type Step = "verify" | "manage" | "create" | "confirm"

function RouteComponent() {
  const navigate = useNavigate()
  const { enabled, isLoading } = usePasscodeEnabled()
  const [step, setStep] = useState<Step | null>(null)
  const [firstCode, setFirstCode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Remount key for PasscodeEntry — bumping it clears the entered digits.
  const [attempt, setAttempt] = useState(0)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!isLoading && step === null) {
      setStep(enabled ? "verify" : "create")
    }
  }, [isLoading, enabled, step])

  const goToStep = (next: Step) => {
    setStep(next)
    setError(null)
    setAttempt((count) => count + 1)
  }

  const onComplete = async (code: string) => {
    setBusy(true)
    try {
      if (step === "verify") {
        if (await verifyPasscode(code)) {
          goToStep("manage")
        } else {
          setError("Wrong passcode. Try again.")
          setAttempt((count) => count + 1)
        }
      } else if (step === "create") {
        setFirstCode(code)
        goToStep("confirm")
      } else if (step === "confirm") {
        if (code === firstCode) {
          await setPasscode(code)
          toast.success(enabled ? "Passcode changed" : "Passcode set")
          void navigate({ to: "/profile/settings" })
        } else {
          setError("Passcodes didn't match. Try again.")
          setAttempt((count) => count + 1)
        }
      }
    } finally {
      setBusy(false)
    }
  }

  const onTurnOff = async () => {
    await removePasscode()
    toast.success("Passcode turned off")
    void navigate({ to: "/profile/settings" })
  }

  const stepTitle =
    step === "verify"
      ? "Enter existing 4 digit passcode"
      : step === "confirm"
        ? "Confirm passcode"
        : "Enter new 4 digit passcode"

  return (
    <AppLayout topBar={<TopBarBackButton title="Passcode" />}>
      {step === "manage" ? (
        <Card className="flex flex-col gap-2 w-full px-2">
          <MenuItem
            leftIcon={<Lock />}
            rightIcon={<ChevronRight />}
            size="large"
            onClick={() => goToStep("create")}
          >
            Change passcode
          </MenuItem>
          <hr className="border-border-L1" />
          <MenuItem
            leftIcon={<LockOpen />}
            variant="destructive"
            size="large"
            onClick={() => void onTurnOff()}
          >
            Turn off passcode
          </MenuItem>
        </Card>
      ) : step !== null ? (
        <div className="flex flex-1 items-center justify-center pb-16">
          <PasscodeEntry
            key={attempt}
            title={stepTitle}
            error={error}
            disabled={busy}
            onComplete={(code) => void onComplete(code)}
          />
        </div>
      ) : null}
    </AppLayout>
  )
}
